class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("#predictive-search");
    this.searchClose = this.querySelector(".search-close");
    this.mobileSearchIcon = this.querySelector(".mobile-search-icon");
    this.mainHeaderSearchWrapper = document.querySelector(
      "#top-search-wrapper"
    );
    this.stickyHeader = document.querySelector(".sticky-header-wrapper");
    this.mainHeaderWrapper = document.querySelector(".header-wrapper");

    // Debounce input handler
    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    //? focus state
    this.input.addEventListener("focus", this.showCloseIconMobile.bind(this));

    // Create intersection observer with header compensation
    const headerHeight = this.stickyHeader?.offsetHeight || 0;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.isIntersecting ? this.open() : this.close()
        );
      },
      {
        root: null,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0.1,
      }
    );
  }

  connectedCallback() {
    document.addEventListener("click", this.close.bind(this));
    this.searchClose?.addEventListener("click", this.closeByBtn.bind(this));
     this?.mobileSearchIcon?.addEventListener("click", () => this?.input?.focus());
    // Observe the results container, not headers
    this.observer.observe(this.predictiveSearchResults);
    // Optional: listen for window resize to update max-height
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      return;
    }
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        throw error;
      });
  }

  getResultsMaxHeight() {
    try {
      const activeHeader = this.stickyHeader?.classList.contains("sticky")
        ? this.stickyHeader
        : this.mainHeaderWrapper;
      return (
        window.innerHeight -
        (activeHeader.getBoundingClientRect().bottom -
          (this.stickyHeader?.classList.contains("sticky")
            ? 70
            : this.mainHeaderWrapper.getBoundingClientRect().bottom / 2))
      );
    } catch (error) {
      console.error("Height calculation error:", error);
      return window.innerHeight * 0.6; // Fallback height
    }
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  showCloseIconMobile() {
    this.mobileSearchIcon.classList.add("hide-search");
    this.searchClose.classList.add("show-close");
  }

  hideCloseIconMobile() {
    this.mobileSearchIcon.classList.remove("hide-search");
    this.searchClose.classList.remove("show-close");
  }

  handleResize() {
    // Update max-height on resize
    if (this.predictiveSearchResults.style.display === "block") {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }
  }

  disconnectedCallback() {
    this.observer.disconnect();
    window.removeEventListener("resize", this.handleResize.bind(this));
  }

  open() {
    // Only open if we have results to show
    if (this.predictiveSearchResults.innerHTML.trim()) {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      this.predictiveSearchResults.style.display = "block";
    }
  }

  closeByBtn(event) {
    event.preventDefault();
    event.stopPropagation();
    this.predictiveSearchResults.style.display = "none";
    this.input.value = "";
    if (this.mainHeaderSearchWrapper?.classList.contains("expanded")) {
      this.mainHeaderSearchWrapper.classList.toggle("expanded", true);
    }
    this.hideCloseIconMobile();
  }

  close(event) {
    if (event && event.target.closest("predictive-search")) return;
    this.predictiveSearchResults.innerHTML = "";
    this.predictiveSearchResults.style.display = "none";
    this.input.value = "";
    if (this.mainHeaderSearchWrapper?.classList.contains("expanded")) {
      this.mainHeaderSearchWrapper.classList.remove("expanded");
    }
    this.hideCloseIconMobile();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("predictive-search", PredictiveSearch);
